import { Head, Link, router } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/ui/pagination";
import { PagyMeta } from "@/types/pagy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Eye, Pencil, Plus, Trash2, Check, X } from "lucide-react";

interface Agency {
  id: number;
  name: string;
  description?: string | null;
  services?: string[] | null;
  working_hours?: WorkingHour[] | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  whatsapp_number?: string | null;
  phone_number?: string | null;
  status?: string | null;
  trusted?: boolean;
  created_at: string;
}

type WorkingHour = {
  day: string;
  opens_at: string;
  closes_at: string;
  closed: boolean;
};

interface Props {
  agencies: Agency[];
  pagy: PagyMeta;
}

export default function Index({ agencies, pagy }: Props) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("ar-UK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const summarizeWorkingHours = (workingHours?: WorkingHour[] | null) => {
    if (!workingHours || workingHours.length === 0) return "—";

    const openDays = workingHours.filter(
      (entry) => !entry.closed && entry.opens_at && entry.closes_at
    );

    if (openDays.length === 0) return "مغلق";

    const [first, ...rest] = openDays;
    const uniform = rest.every(
      (entry) => entry.opens_at === first.opens_at && entry.closes_at === first.closes_at
    );

    if (uniform) {
      return `يوميًا ${first.opens_at} - ${first.closes_at}`;
    }

    return `${openDays.length} أيام محددة`;
  };

  const handleDelete = (id: number) => {
    router.delete(`/desk/agencies/${id}`);
  };

  const handleStatusChange = (id: number, status: "active" | "suspended") => {
    router.patch(
      `/desk/agencies/${id}`,
      { agency: { status } },
      { preserveScroll: true, preserveState: true }
    );
  };

  const STATUS_LABELS: Record<string, string> = {
    active: "مفعلة",
    inactive: "غير فعالة",
    pending_verification: "قيد التحقق",
    suspended: "موقوفة",
  };

  const STATUS_STYLES: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    inactive: "bg-slate-100 text-slate-800 border-slate-200",
    pending_verification: "bg-amber-100 text-amber-800 border-amber-200",
    suspended: "bg-rose-100 text-rose-800 border-rose-200",
  };

  return (
    <>
      <Head title="الوكالات العقارية" />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>الوكالات العقارية</CardTitle>
              <CardDescription>إدارة قائمة الوكالات وعرض تفاصيلها</CardDescription>
            </div>
            <Button asChild>
              <Link href="/desk/agencies/new">
                <Plus className="ms-2 h-4 w-4" />
                إضافة وكالة جديدة
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {agencies.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              لا توجد وكالات مسجلة حالياً
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>الاسم</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>موثوقة</TableHead>
                  <TableHead>رقم الهاتف</TableHead>
                  <TableHead>ساعات العمل</TableHead>
                  <TableHead>تاريخ الإضافة</TableHead>
                  <TableHead className="w-[160px]">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agencies.map((agency) => (
                  <TableRow key={agency.id}>
                    <TableCell>{agency.name}</TableCell>
                    <TableCell>
                      {agency.status ? (
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                            STATUS_STYLES[agency.status] ||
                            "bg-muted/20 border-muted text-muted-foreground"
                          }`}
                        >
                          {STATUS_LABELS[agency.status] || agency.status}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.trusted ? (
                        <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-100 px-3 py-0.5 text-xs font-semibold text-sky-800">
                          موثوقة
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{agency.phone_number || "—"}</TableCell>
                    <TableCell>{summarizeWorkingHours(agency.working_hours)}</TableCell>
                    <TableCell>{formatDate(agency.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" asChild>
                          <Link href={`/desk/agencies/${agency.id}`}>
                            <Eye className="h-4 w-4" />
                            <span className="sr-only">عرض</span>
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <Link href={`/desk/agencies/${agency.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">تعديل</span>
                          </Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">حذف</span>
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
                              <AlertDialogDescription>
                                هل أنت متأكد من حذف الوكالة <strong>{agency.name}</strong>؟
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleDelete(agency.id)}
                              >
                                حذف
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        {agency.status === "pending_verification" && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStatusChange(agency.id, "active")}
                              className="text-emerald-600"
                            >
                              <Check className="h-4 w-4" />
                              <span className="sr-only">اعتماد</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStatusChange(agency.id, "suspended")}
                              className="text-rose-600"
                            >
                              <X className="h-4 w-4" />
                              <span className="sr-only">أيقاف</span>
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination pagy={pagy} basePath="/desk/agencies" />
    </>
  );
}
